import React, { useMemo } from "react";
import { MockedProvider } from "@apollo/client/testing/react";
import type { Meta, StoryObj } from "@storybook/react";
import CreateMissionStack from "../../../navigations/CreateMissionStack";
import {
    buildActivityType,
    buildClient,
    buildClientInfo,
    buildClientProfile,
    buildIjmGroup,
    buildMission,
    buildSalaryForecast,
    buildUser,
    buildWorkerInfo,
    CCTS_FIELD_VALUES,
    CCTS_FIELDS,
    CCTS_WITH_LSE,
    IJM_CHOICES_MOCK,
} from "../../factories";
import { GET_CLIENT_WORKERS_AND_INVITATION_WORKERS } from "../../../graphql/client";
import { MockedAuthManager } from "../../../contexts/AuthManager";
import { Cct, EarningType, Mission, ProfileType } from "../../../__generated__/graphql";
import { RootStackParamList } from "../../../navigations/RootNavigation";
import { FIND_ACTIVITY_TYPE, CREATE_MISSION, GET_MISSION } from "../../../graphql/core";
import {
    FIND_CCT,
    GET_CCT_BY_EDITION_BY,
    GET_CCT_DETAILS_FIELD_VALUES_FROM_PARENT,
    GET_CCT_DETAILS_FIELDS,
} from "../../../graphql/cct";
import { GET_SALARY_FORECAST } from "../../../graphql/salaries";
import { buildGraphqlMock_GET_SALARY_DETAILS } from "../../mock-factories";
import { createNativeStackNavigator } from "@react-navigation/native-stack";
import SalaryCountingDetailsScreen from "../../../screens/SalaryCountingDetails";
import { MockedProfileSwitcherManager } from "../../../contexts/ProfileSwitcherManager";
import IJMEditorNavScreen from "../../../screens/client/IJMEditorNavScreen";
import { StoryStackNavigatorContainer } from "../../display-helpers";
import { MockLink } from "@apollo/client/testing";
import { Buildable } from "ts-essentials";
import { CreateMissionMode } from "../../../contexts/CreateMissionManager";
import { IjmSelection } from "../../../utils";

type StoryStackParams = {
    CreateMissionStack?: {
        initialMission?: Buildable<Mission>;
        createMissionMode?: CreateMissionMode;
    };
    SalaryCountingDetails: RootStackParamList["SalaryCountingDetails"];
    IJMEditorNavScreen: {
        ijmSelection?: Buildable<IjmSelection> | undefined;
        salaryInfo?:
            | {
                  amount: number;
                  earningType: EarningType;
              }
            | undefined;
        cct?: Cct;
        useLse: boolean;
    };
};

const Stack = createNativeStackNavigator<StoryStackParams>();
function StoryComponent() {
    const user = useMemo(
        () =>
            buildUser({
                clientInfos: [buildClientInfo({ id: "client" })],
                clientProfiles: [
                    buildClientProfile({
                        id: "client",
                        client: buildClient({
                            ijmGroup: buildIjmGroup({
                                id: "group-1",
                            }),
                            ijmPayFirstDays: true,
                            ijmPayRemainingPercents: 10,
                        }),
                    }),
                ],
            }),
        [],
    );
    const workerInfo = useMemo(() => buildWorkerInfo(), []);
    const mission = useMemo(() => buildMission({}), []);

    const mocks = useMemo<MockLink.MockedResponse[]>(
        () => [
            {
                request: {
                    query: FIND_ACTIVITY_TYPE,
                    variables: () => true,
                },
                result: () => ({
                    data: {
                        findActivityType: {
                            pageInfo: {
                                totalElements: 1,
                                hasNextPage: false,
                                hasPreviousPage: false,
                                startCursor: "fake-start-cursor",
                                endCursor: "fake-end-cursor",
                            },
                            elements: [buildActivityType()],
                        },
                    },
                }),
            },
            {
                request: {
                    query: GET_CLIENT_WORKERS_AND_INVITATION_WORKERS,
                    variables: () => true,
                },
                result: () => ({
                    data: {
                        getClientWorkers: [
                            buildWorkerInfo({
                                id: "worker-profile-1",
                            }),
                            workerInfo,
                        ],
                        workerInvitationsSent: [],
                    },
                }),
            },
            {
                request: {
                    query: FIND_CCT,
                    variables: () => true,
                },
                result: () => {
                    return {
                        data: {
                            findCct: {
                                elements: Object.values(CCTS_WITH_LSE),
                                pageInfo: {
                                    totalElements: CCTS_WITH_LSE.length,
                                },
                            },
                        },
                    };
                },
            },
            {
                request: {
                    query: GET_CCT_BY_EDITION_BY,
                    variables: () => true,
                },
                result: ({ cctEditionId }: { cctEditionId: string }) => ({
                    data: {
                        getCctByEditionId: CCTS_WITH_LSE[cctEditionId],
                    },
                }),
            },
            {
                request: {
                    query: GET_CCT_DETAILS_FIELDS,
                    variables: () => true,
                },

                result: ({ cctEditionId }: { cctEditionId: string }) => {
                    return {
                        data: {
                            getCctDetailsFields: CCTS_FIELDS[cctEditionId],
                        },
                    };
                },
            },
            {
                request: {
                    query: GET_CCT_DETAILS_FIELD_VALUES_FROM_PARENT,
                    variables: () => true,
                },
                result: ({
                    cctEditionId,
                    criteriaStructureId,
                }: {
                    cctEditionId: string;
                    criteriaStructureId: number;
                }) => {
                    const key = `${cctEditionId}-${criteriaStructureId}`;
                    return {
                        data: {
                            getCctDetailsFieldValues: CCTS_FIELD_VALUES[key],
                        },
                    };
                },
            },
            buildGraphqlMock_GET_SALARY_DETAILS(),
            {
                request: {
                    query: GET_SALARY_FORECAST,
                    variables: () => true,
                },
                result: () => ({
                    data: { getSalaryForecast: [buildSalaryForecast()] },
                }),
            },
            {
                request: {
                    query: CREATE_MISSION,
                    variables: () => true,
                },
                result: (props: Record<string, string | number | null | object>) => {
                    // eslint-disable-next-line no-console
                    console.log("createMission ->\n", JSON.stringify(props, null, 2));
                    return {
                        data: { createMission: mission },
                    };
                },
            },
            {
                request: {
                    query: GET_MISSION,
                    variables: () => true,
                },
                result: () => ({
                    data: { getMission: mission },
                }),
            },
            IJM_CHOICES_MOCK,
        ],
        [mission, workerInfo],
    );
    return (
        <MockedAuthManager user={user}>
            <MockedProfileSwitcherManager
                profileType={ProfileType.Client}
                profileId={ProfileType.Client}
            >
                <MockedProvider mocks={mocks}>
                    <StoryStackNavigatorContainer>
                        <Stack.Navigator initialRouteName="CreateMissionStack">
                            <Stack.Screen
                                name="CreateMissionStack"
                                component={CreateMissionStack}
                                initialParams={{ initialMission: { workerInfo } }}
                                options={{
                                    headerShown: false,
                                    gestureEnabled: false,
                                }}
                            />
                            <Stack.Screen
                                name="SalaryCountingDetails"
                                component={SalaryCountingDetailsScreen}
                                options={{
                                    headerShown: false,
                                    gestureEnabled: true,
                                    presentation: "modal",
                                }}
                            />
                            <Stack.Screen
                                name="IJMEditorNavScreen"
                                component={IJMEditorNavScreen}
                                options={{
                                    headerShown: false,
                                    gestureEnabled: true,
                                    animation: "slide_from_bottom",
                                }}
                            />
                        </Stack.Navigator>
                    </StoryStackNavigatorContainer>
                </MockedProvider>
            </MockedProfileSwitcherManager>
        </MockedAuthManager>
    );
}

const meta = {
    title: "CreateMission/FULL",
    component: StoryComponent,
    argTypes: {},
} satisfies Meta<typeof StoryComponent>;

export default meta;

type Story = StoryObj<typeof meta>;

export const Default: Story = {};
